import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function CortesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = isDark ? 'dark' : 'light';
  const [activeTab, setActiveTab] = useState('retirar');
  const [acumulativoSellers, setAcumulativoSellers] = useState([]);
  const [porSaldoSellers, setPorSaldoSellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para los formularios
  const [selectedSeller, setSelectedSeller] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchSellers();
  }, [activeTab]);

  const fetchSellers = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const endpoint = activeTab === 'retirar' 
        ? '/api/cortes/sellers/acumulativo' 
        : '/api/cortes/sellers/por-saldo';

      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (activeTab === 'retirar') {
          setAcumulativoSellers(data);
        } else {
          setPorSaldoSellers(data);
        }
      } else {
        setError('Error al cargar vendedores');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const seller = acumulativoSellers.find(s => s.id === selectedSeller);
    if (!seller) {
      setError('Vendedor no encontrado');
      setLoading(false);
      return;
    }

    if (parseFloat(amount) <= 0) {
      setError('El monto debe ser mayor a 0');
      setLoading(false);
      return;
    }

    if (parseFloat(amount) > seller.current_balance) {
      setError('El monto no puede ser mayor al saldo disponible');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cortes/withdraw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          seller_id: selectedSeller,
          amount: parseFloat(amount),
          notes: notes || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Retiro exitoso:\n` +
          `- Monto retirado: $${data.amount_withdrawn.toFixed(2)}\n` +
          `- Comisión vendedor: $${data.seller_commission.toFixed(2)}\n` +
          `- Admin recibe: $${data.admin_receives.toFixed(2)}\n` +
          `- Nuevo saldo: $${data.new_balance.toFixed(2)}`
        );
        
        // Reset form
        setSelectedSeller('');
        setAmount('');
        setNotes('');
        
        // Recargar vendedores
        fetchSellers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al procesar retiro');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const seller = porSaldoSellers.find(s => s.id === selectedSeller);
    if (!seller) {
      setError('Vendedor no encontrado');
      setLoading(false);
      return;
    }

    if (parseFloat(amount) <= 0) {
      setError('El monto debe ser mayor a 0');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cortes/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          seller_id: selectedSeller,
          amount: parseFloat(amount),
          notes: notes || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Abono exitoso:\n` +
          `- Monto abonado: $${data.amount_deposited.toFixed(2)}\n` +
          `- Comisión agregada: $${data.commission_added.toFixed(2)}\n` +
          `- Total agregado: $${data.total_added.toFixed(2)}\n` +
          `- Nuevo saldo: $${data.new_balance.toFixed(2)}`
        );
        
        // Reset form
        setSelectedSeller('');
        setAmount('');
        setNotes('');
        
        // Recargar vendedores
        fetchSellers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al procesar abono');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const currentSellers = activeTab === 'retirar' ? acumulativoSellers : porSaldoSellers;
  const selectedSellerData = currentSellers.find(s => s.id === selectedSeller);

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className={`shadow-sm transition-colors duration-300 ${
        isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
              }`}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Cortes de Caja
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Gestionar retiros y abonos de vendedores
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => {
              setActiveTab('retirar');
              setSelectedSeller('');
              setAmount('');
              setNotes('');
              setError('');
              setSuccess('');
            }}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'retirar'
                ? theme === 'dark'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-500 text-white'
                : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Retirar (Acumulativo)
          </button>
          <button
            onClick={() => {
              setActiveTab('abonar');
              setSelectedSeller('');
              setAmount('');
              setNotes('');
              setError('');
              setSuccess('');
            }}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'abonar'
                ? theme === 'dark'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-500 text-white'
                : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Abonar (Por Saldo)
          </button>
        </div>

        {/* Contenido */}
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
          {loading && !currentSellers.length && (
            <div className="text-center py-8">
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Cargando vendedores...</p>
            </div>
          )}

          {!loading && currentSellers.length === 0 && (
            <div className="text-center py-8">
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                No hay vendedores tipo {activeTab === 'retirar' ? 'Acumulativo' : 'Por Saldo'}
              </p>
            </div>
          )}

          {currentSellers.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Lista de Vendedores */}
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                  Vendedores {activeTab === 'retirar' ? 'Acumulativos' : 'Por Saldo'}
                </h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {currentSellers.map((seller) => (
                    <div
                      key={seller.id}
                      onClick={() => setSelectedSeller(seller.id)}
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        selectedSeller === seller.id
                          ? theme === 'dark'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-500 text-white'
                          : theme === 'dark'
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold">{seller.name}</p>
                          <p className="text-sm opacity-75">{seller.phone}</p>
                          <p className="text-xs opacity-75">Comisión: {seller.commission_percentage}%</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">${seller.current_balance.toFixed(2)}</p>
                          <p className="text-xs opacity-75">Saldo actual</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Formulario */}
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                  {activeTab === 'retirar' ? 'Realizar Retiro' : 'Realizar Abono'}
                </h2>

                {error && (
                  <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg whitespace-pre-line">
                    {success}
                  </div>
                )}

                <form onSubmit={activeTab === 'retirar' ? handleWithdraw : handleDeposit} className="space-y-4">
                  {!selectedSeller && (
                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                      Selecciona un vendedor de la lista
                    </div>
                  )}

                  {selectedSeller && selectedSellerData && (
                    <>
                      <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                          {selectedSellerData.name}
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          Saldo actual: <span className="font-bold">${selectedSellerData.current_balance.toFixed(2)}</span>
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          Comisión: {selectedSellerData.commission_percentage}%
                        </p>
                      </div>

                      <div>
                        <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                          Monto *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                          className={`w-full px-4 py-2 rounded-lg border ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-800'
                          } focus:ring-2 focus:ring-blue-500`}
                          placeholder="0.00"
                        />
                      </div>

                      {activeTab === 'retirar' && amount && (
                        <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                          <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                            <strong>Cálculo del retiro:</strong>
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                            • Comisión vendedor ({selectedSellerData.commission_percentage}%): ${(parseFloat(amount) * selectedSellerData.commission_percentage / 100).toFixed(2)}
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                            • Admin recibe: ${(parseFloat(amount) - (parseFloat(amount) * selectedSellerData.commission_percentage / 100)).toFixed(2)}
                          </p>
                          <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                            • Nuevo saldo: ${(selectedSellerData.current_balance - parseFloat(amount) + (parseFloat(amount) * selectedSellerData.commission_percentage / 100)).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {activeTab === 'abonar' && amount && (
                        <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                          <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                            <strong>Cálculo del abono:</strong>
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                            • Monto base: ${parseFloat(amount).toFixed(2)}
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                            • Comisión agregada ({selectedSellerData.commission_percentage}%): ${(parseFloat(amount) * selectedSellerData.commission_percentage / 100).toFixed(2)}
                          </p>
                          <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                            • Total a agregar: ${(parseFloat(amount) * (1 + selectedSellerData.commission_percentage / 100)).toFixed(2)}
                          </p>
                          <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}>
                            • Nuevo saldo: ${(selectedSellerData.current_balance + parseFloat(amount) * (1 + selectedSellerData.commission_percentage / 100)).toFixed(2)}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className={`block mb-2 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                          Notas (opcional)
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows="3"
                          className={`w-full px-4 py-2 rounded-lg border ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-800'
                          } focus:ring-2 focus:ring-blue-500`}
                          placeholder="Notas adicionales..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading || !amount}
                        className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                          loading || !amount
                            ? 'bg-gray-400 cursor-not-allowed'
                            : activeTab === 'retirar'
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {loading ? 'Procesando...' : activeTab === 'retirar' ? 'Realizar Retiro' : 'Realizar Abono'}
                      </button>
                    </>
                  )}
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CortesPage;
